import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FileText, Loader2 } from "lucide-react";
import { useCreateInsuredNote, useCreatePolicyNote } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/trackEvent";

// CSR-771 — Add Note modal. Shared by the insured Activity Timeline and the
// policy Activities tab. The `scope` prop discriminates the two paths (insured
// vs. policy); copy and the underlying mutation differ but the form layout is
// identical.
//
// Verbatim copy locked at the spec level. QA diffs the strings byte-for-byte.

const TITLE_MAX = 200;
const TITLE_COUNTER_THRESHOLD = 150;

export type AddNoteModalProps =
  | { scope: "insured"; insuredId: string; trigger?: ReactNode }
  | { scope: "policy"; policyId: string; insuredId: string; trigger?: ReactNode };

interface CurrentUserPayload {
  id?: string;
}

export function AddNoteModal(props: AddNoteModalProps) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [showError, setShowError] = useState(false);
  const { toast } = useToast();

  // Both mutations expose the same (almost) shape but type-narrow per scope.
  const createInsured = useCreateInsuredNote(
    props.scope === "insured" ? props.insuredId : "",
  );
  const createPolicy = useCreatePolicyNote(
    props.scope === "policy" ? props.policyId : "",
    props.scope === "policy" ? props.insuredId : "",
  );
  const isPending =
    props.scope === "insured" ? createInsured.isPending : createPolicy.isPending;

  const subtitle =
    props.scope === "insured"
      ? "Add a note to this insured's activity timeline."
      : "Add a note to this policy's activity timeline.";

  const reset = () => {
    setTitle("");
    setDetails("");
    setShowError(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (next) {
      trackEvent("Add Note Modal Opened", { scope: props.scope });
    } else {
      // Closing the modal — fire cancelled event with had_content
      const hadContent = title.trim().length > 0 || details.trim().length > 0;
      trackEvent("Add Note Modal Cancelled", {
        scope: props.scope,
        had_content: hadContent,
      });
      reset();
    }
    setOpen(next);
  };

  const handleTitleChange = (value: string) => {
    // Hard cap at TITLE_MAX (defense-in-depth — also enforced via maxLength).
    if (value.length > TITLE_MAX) {
      setTitle(value.slice(0, TITLE_MAX));
    } else {
      setTitle(value);
    }
    if (showError && value.trim().length > 0) {
      setShowError(false);
    }
  };

  const submit = () => {
    const t = title.trim();
    if (!t) {
      setShowError(true);
      trackEvent("Note Validation Error", { scope: props.scope });
      return;
    }
    const detailsValue = details.trim() || null;

    const payload = { title: t, details: detailsValue };
    const onSuccess = () => {
      reset();
      setOpen(false);
      toast({
        title: "Note Added",
        description: "Your note has been added to the timeline.",
      });
      // We don't have direct access to user id here — pass the id field if
      // the upstream caller injected it via `dataset` later. v1: leave null.
      const userId = (window as any)?.__currentUser?.id ?? null;
      trackEvent("Note Created", {
        scope: props.scope,
        has_details: !!detailsValue,
        author_id: userId,
      });
    };
    const onError = (err: unknown) => {
      const status = (err as { status?: number })?.status ?? 0;
      const errorType =
        status >= 500 ? "server" : status === 422 ? "validation" : "network";
      toast({
        title: "Failed to save note. Please try again.",
        variant: "destructive",
      });
      trackEvent("Note Save Failed", {
        scope: props.scope,
        error_type: errorType,
      });
    };

    if (props.scope === "insured") {
      createInsured.mutate(payload, { onSuccess, onError });
    } else {
      createPolicy.mutate(payload, { onSuccess, onError });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {props.trigger ?? (
          <Button
            variant="outline"
            size="sm"
            className="gap-2 rounded-full"
            data-testid="add-note-button"
          >
            <FileText className="w-4 h-4" /> Add Note
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-md"
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          // Plain Enter in the textarea adds a newline; Cmd/Ctrl+Enter still
          // submits from anywhere. Plain Enter from the title input (or other
          // non-textarea focus) submits — this is what surfaces the inline
          // `Title is required` error when the title is empty.
          const isTextarea =
            (e.target as HTMLElement | null)?.tagName === "TEXTAREA";
          if ((e.metaKey || e.ctrlKey) || !isTextarea) {
            e.preventDefault();
            submit();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Add Note</DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="add-note-title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="add-note-title"
              autoFocus
              placeholder="Note title..."
              value={title}
              maxLength={TITLE_MAX}
              onChange={(e) => handleTitleChange(e.target.value)}
              className="rounded-lg h-10"
              aria-invalid={showError}
              aria-describedby={showError ? "add-note-title-error" : undefined}
              data-testid="add-note-title"
            />
            {showError && (
              <p
                id="add-note-title-error"
                role="alert"
                className="text-xs text-destructive"
                data-testid="add-note-title-error"
              >
                Title is required
              </p>
            )}
            {title.length >= TITLE_COUNTER_THRESHOLD && (
              <p className="text-xs text-muted-foreground" data-testid="add-note-title-counter">
                {title.length}/{TITLE_MAX}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-note-details">Details</Label>
            <Textarea
              id="add-note-details"
              placeholder="Write your note here..."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              className="min-h-[96px] resize-none rounded-lg"
              data-testid="add-note-details"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
            data-testid="add-note-cancel"
          >
            Cancel
          </Button>
          <Button
            className="rounded-full gap-2"
            onClick={submit}
            // CSR-790 D-1 — keep the button clickable on empty title so the
            // submit handler can render the inline `Title is required` error.
            // Disabling on `!title.trim()` prevented the validation path from
            // ever firing.
            disabled={isPending}
            data-testid="add-note-submit"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Note
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
